import type { Translations } from "./en";

/** Use-case pages (`/cas-usage/*`), imported into the French dictionary. */
export const useCasesFr: Translations["useCases"] = {
	index: {
		hero: {
			label:
				"Cas d’usage · Maintenance · QHSE · Propreté · Applications mobiles",
			title: "Des logiciels pensés pour un métier précis.",
			emphasis: "un métier précis",
			intro:
				"Maintenance CVC, cabinets QHSE, entreprises de propreté, applications mobiles à reprendre : pour chaque situation, la journée type, ce que le métier exige et l’outil que Lezo construirait.",
		},
		list: {
			label: "Quatre situations",
			title: "Choisissez la situation la plus proche de la vôtre",
			description:
				"Chaque page part d’une journée de travail réelle, des obligations du métier et des outils du marché, puis montre l’outil étape par étape.",
		},
		audienceLabel: "Pour qui",
		link: "Voir le cas d’usage",
		note: {
			title: "Des scénarios types, pas des références clients",
			description:
				"Chaque cas d’usage décrit un métier tel que nous l’avons étudié : obligations, journée type, outils du marché. Les écrans sont des illustrations aux données fictives. Votre processus réel reste le point de départ du cadrage.",
		},
		other: {
			title: "Votre métier n’est pas dans la liste ?",
			description:
				"La méthode est la même : partir de la journée de vos équipes et de ce que vos clients attendent. Décrivez-nous votre fonctionnement, nous vous dirons si un outil sur mesure se justifie.",
		},
		cta: {
			title: "Laquelle de ces situations ressemble à la vôtre ?",
			description:
				"Décrivez votre métier et la façon dont votre équipe travaille aujourd’hui. Nous vous répondons pour en discuter.",
			button: "Décrire ma situation",
		},
	},
	common: {
		frictionLabel: "Point de friction",
		referenceLabel: "Référence",
		related: {
			label: "Autres cas d’usage",
			title: "D’autres métiers, la même méthode",
			link: "Voir le cas d’usage",
			all: "Tous les cas d’usage",
		},
	},
	items: {
		hvac: {
			card: {
				name: "Maintenance CVC",
				navDescription:
					"Bons d’intervention, attestations d’entretien et portail syndic.",
				title:
					"Du bon d’intervention à l’attestation d’entretien, sans ressaisie",
				summary:
					"Pour les entreprises de maintenance chauffage, ventilation et climatisation qui gèrent des contrats d’entretien avec des syndics, des bailleurs et des collectivités.",
				audience:
					"Dirigeants et responsables d’exploitation, 10 à 40 techniciens",
			},
			hero: {
				label: "Cas d’usage · Maintenance CVC · Chauffage · Climatisation",
				title:
					"Un logiciel de maintenance CVC fait pour vos contrats d’entretien.",
				emphasis: "vos contrats d’entretien",
				intro:
					"Planning des visites, relevés en chaufferie, attestations d’entretien, fiches fluides, rapports pour les syndics : Lezo réunit vos interventions dans une application à votre image, pour un coût qui ne dépend pas du nombre de techniciens.",
				primaryCta: "Décrire mon organisation",
				secondaryCta: "Voir l’outil étape par étape",
			},
			facts: {
				who: {
					label: "Pour qui",
					value:
						"Entreprises de maintenance CVC et multitechnique, 10 à 40 techniciens",
				},
				tool: {
					label: "Ce que l’on outille",
					value:
						"Planning, relevés terrain, attestations, fiches fluides, portail client",
				},
				first: {
					label: "Première version",
					value:
						"Relevé sur mobile et attestation générée, pour un type de contrat",
				},
			},
			day: {
				label: "Une journée type",
				title: "Une journée de maintenance, aujourd’hui",
				description:
					"Le scénario que nous retrouvons le plus souvent dans les entreprises de 10 à 40 techniciens. Chaque étape fonctionne, mais l’information change trois fois de support avant d’arriver au client.",
				moments: {
					a: {
						time: "07:30",
						actor: "Technicien",
						text: "Karim récupère ses ordres de travail du jour : trois visites préventives, et un dépannage ajouté la veille au soir par SMS.",
						friction:
							"Le planning vit dans un tableur que seule l’assistante modifie.",
					},
					b: {
						time: "09:15",
						actor: "Technicien · chaufferie",
						text: "Dans la chaufferie d’une résidence, il relève pression, températures et analyse de combustion sur un bon papier.",
						friction:
							"Pas de réseau en sous-sol : les photos restent sur son téléphone personnel.",
					},
					c: {
						time: "11:40",
						actor: "Technicien · toiture",
						text: "Contrôle d’étanchéité d’un rooftop : la charge de fluide et le résultat vont sur une fiche Cerfa remplie à la main.",
						friction:
							"La prochaine échéance de contrôle est notée dans un autre tableur.",
					},
					d: {
						time: "17:30",
						actor: "Assistante d’exploitation",
						text: "Julie ressaisit les bons de la journée dans le logiciel de facturation et relance deux techniciens pour des bons manquants.",
						friction:
							"Une à deux heures de ressaisie par jour, et la facturation attend.",
					},
					e: {
						time: "Fin de saison",
						actor: "Direction",
						text: "Un syndic demande le bilan annuel de ses douze résidences, avec les attestations d’entretien et les dates de passage.",
						friction:
							"Il faut reconstituer la saison à partir de PDF, de photos et d’e-mails.",
					},
				},
			},
			stakes: {
				label: "Ce que le métier exige",
				title: "Des obligations qui produisent des documents",
				description:
					"Chaque visite doit laisser une trace précise, dans un délai donné. C’est là que se perd le temps, et là qu’un outil adapté fait la différence.",
				items: {
					attestation: {
						title: "L’attestation d’entretien sous 15 jours",
						description:
							"Chaque chaudière de 4 à 400 kW est entretenue une fois par an. L’attestation, avec le rendement et les émissions mesurées, est remise dans les 15 jours qui suivent la visite.",
						ref: "Décret n° 2009-649 · arrêté du 15 septembre 2009",
					},
					fluids: {
						title: "Le suivi des fluides frigorigènes",
						description:
							"Chaque intervention sur un équipement frigorifique donne lieu à une fiche d’intervention, et la fréquence des contrôles d’étanchéité dépend de la charge en tonnes équivalent CO2.",
						ref: "Règlement (UE) 2024/573 · Cerfa n° 15497",
					},
					schedule: {
						title: "Une échéance différente par équipement",
						description:
							"Entretien annuel des chaudières, tous les deux ans pour les pompes à chaleur et la climatisation, inspection tous les cinq ans au-delà de 70 kW : chaque équipement a son calendrier.",
						ref: "Décrets n° 2009-649 et n° 2020-912",
					},
				},
			},
			build: {
				label: "Ce que Lezo construit",
				title: "Un seul outil, du planning au rapport client",
				description:
					"Un exemple d’application pour une entreprise de maintenance. La vôtre part de vos contrats, de vos fiches de relevés et de votre vocabulaire.",
				steps: {
					a: {
						title: "Le planning se construit à partir des contrats",
						description:
							"Les visites prévues par chaque contrat apparaissent d’elles-mêmes dans le planning. L’assistante n’ajoute que les dépannages et répartit les techniciens.",
					},
					b: {
						title: "Le relevé se fait en chaufferie, même sans réseau",
						description:
							"Le technicien saisit ses mesures dans une fiche qui reprend votre bon d’intervention. Photos et signature du gardien sont jointes, puis tout se synchronise à la sortie.",
					},
					c: {
						title: "L’attestation part le jour même",
						description:
							"Les mesures du relevé alimentent l’attestation d’entretien et la fiche fluides. Le document signé part au client sans ressaisie, bien avant l’échéance des 15 jours.",
					},
					d: {
						title: "Le syndic suit ses résidences sans vous appeler",
						description:
							"Dans son espace, le gestionnaire voit les visites réalisées, télécharge les attestations et retrouve le bilan de saison de chacune de ses résidences.",
					},
				},
			},
			why: {
				label: "Pourquoi du sur-mesure",
				title: "Pourquoi un outil fait pour votre entreprise",
				description:
					"Les logiciels d’intervention du marché se paient par technicien et par mois, et imposent leur modèle de bon. Un outil à vous suit vos contrats et ne coûte pas plus cher quand l’équipe grandit.",
				items: {
					forms: {
						title: "Vos fiches, pas celles d’un éditeur",
						description:
							"Vos relevés, vos prestations P2 et P3, vos libellés : l’outil reprend vos documents actuels au lieu de vous imposer les siens.",
					},
					proof: {
						title: "Une preuve de passage opposable",
						description:
							"Heure, photos et signature sur chaque intervention : de quoi répondre à un litige sur un délai ou une pénalité.",
					},
					growth: {
						title: "Recruter sans payer de licence",
						description:
							"Le coût ne dépend pas du nombre de techniciens. Embaucher pour la saison de chauffe ne change pas la facture.",
					},
					offline: {
						title: "Pensé pour les chaufferies",
						description:
							"La saisie fonctionne hors réseau et se synchronise dès que le téléphone retrouve une connexion.",
					},
				},
				visual: {
					title: "Logiciel d’intervention ou votre outil, sur six ans",
					subscription: "Logiciel d’intervention par abonnement",
					note: "Hypothèse indicative : 25 utilisateurs à 50 € par mois, dans la fourchette des logiciels d’intervention du marché.",
				},
			},
			scope: {
				first: {
					label: "Première version",
					title: "Ce que contiendrait une première version",
					items: {
						a: "La fiche de relevé de votre principal type de visite, utilisable hors réseau.",
						b: "L’attestation d’entretien générée à partir du relevé, à votre en-tête.",
						c: "Le planning des visites issues des contrats d’entretien.",
						d: "La reprise de votre parc : sites, équipements et contrats en cours.",
					},
				},
				check: {
					label: "Au cadrage",
					title: "Ce que nous vérifions ensemble",
					items: {
						a: "Les types de visites, leurs fiches et leur fréquence réelle.",
						b: "Le logiciel de facturation ou la GMAO avec lesquels l’outil échange.",
						c: "Les documents attendus par vos clients : attestations, bilans, fiches fluides.",
						d: "Le portail client : demande réelle de vos syndics et bailleurs, ou évolution ultérieure.",
					},
				},
			},
			faq: {
				title: "Vos questions sur un outil de maintenance sur mesure",
				items: {
					saas: {
						question: "Pourquoi pas un logiciel d’intervention du marché ?",
						answer:
							"C’est parfois la bonne réponse, et le cadrage compare les options. Le sur-mesure se justifie quand vos fiches et vos contrats ne rentrent pas dans le modèle de l’éditeur, ou quand le coût par technicien devient élevé : 25 utilisateurs à 50 € par mois représentent 15 000 € par an.",
					},
					offline: {
						question:
							"L’application fonctionne-t-elle en chaufferie, sans réseau ?",
						answer:
							"Oui, c’est une exigence de départ. Relevés, photos et signatures sont enregistrés sur le téléphone et envoyés automatiquement dès que la connexion revient.",
					},
					adoption: {
						question: "Mes techniciens vont-ils l’utiliser ?",
						answer:
							"La fiche mobile reprend le bon qu’ils connaissent, avec moins de champs à remplir. Deux ou trois techniciens la testent sur de vraies visites avant le déploiement, et leurs retours sont intégrés.",
					},
					documents: {
						question: "Les attestations générées sont-elles conformes ?",
						answer:
							"Le modèle reprend les mentions prévues par l’arrêté du 15 septembre 2009 et votre propre présentation. Il est validé avec vous avant la mise en service ; les mesures restent sous la responsabilité du technicien qui signe.",
					},
					data: {
						question: "Que deviennent nos contrats et notre parc existants ?",
						answer:
							"Sites, équipements et contrats sont repris depuis vos fichiers actuels lors de la mise en service. Le cadrage précise ce qui est importé et comment.",
					},
				},
			},
			cta: {
				title: "Comment s’organisent vos visites aujourd’hui ?",
				description:
					"Combien de techniciens, quels contrats, quels documents vos clients attendent ? Quelques lignes suffisent pour préparer l’échange.",
				button: "Décrire mon organisation",
			},
			scene: {
				labels: {
					planning: "Planning",
					survey: "Relevé",
					certificate: "Attestation",
					portal: "Portail syndic",
				},
				host: "maintenance.votre-entreprise.fr",
				planning: {
					title: "Planning · semaine 42",
					fromContract: "Contrat",
					urgent: "Dépannage",
					days: {
						mon: "Lun.",
						tue: "Mar.",
						wed: "Mer.",
						thu: "Jeu.",
						fri: "Ven.",
					},
					visits: {
						a: { site: "Les Tilleuls", type: "Entretien annuel", tech: "KB" },
						b: { site: "Quai 12", type: "Étanchéité rooftop", tech: "JR" },
						c: { site: "Les Glycines", type: "Panne ECS", tech: "KB" },
						d: { site: "École Lurçat", type: "Mise en route", tech: "MD" },
						e: { site: "Le Parc", type: "Entretien annuel", tech: "JR" },
					},
				},
				survey: {
					title: "OT 2026-1147",
					place: "Chaufferie gaz · Les Tilleuls",
					offline: "Hors réseau",
					synced: "Synchronisé",
					sign: "Signature du gardien",
					fields: {
						co: { label: "CO ambiant", value: "2 ppm" },
						co2: { label: "CO2", value: "9,1 %" },
						flue: { label: "Fumées", value: "54 °C" },
						efficiency: { label: "Rendement", value: "98,6 %" },
					},
					checks: {
						burner: "Brûleur nettoyé",
						vessel: "Vase d’expansion à 1,5 bar",
					},
				},
				certificate: {
					title: "Attestation d’entretien",
					client: "Résidence Les Tilleuls · syndic Cabinet Morvan",
					checksTitle: "Points contrôlés",
					boiler: "Chaudière gaz à condensation · 225 kW",
					signed: "Signé par K. B.",
					sent: "Envoyée · J+0",
				},
				portal: {
					title: "Cabinet Morvan · Vos résidences",
					season: "Saison de chauffe 2026-2027",
					progress: "Visites réalisées",
					progressValue: "38 / 42",
					download: "Attestation",
					rows: {
						a: { site: "Résidence Les Tilleuls", status: "Faite · 14/10" },
						b: { site: "Résidence Le Parc", status: "Faite · 13/10" },
						c: { site: "Les Jardins d’Anjou", status: "Prévue · 21/10" },
					},
				},
			},
		},
		qhse: {
			card: {
				name: "Cabinet QHSE",
				navDescription:
					"Audits, plans d’action et portail de suivi pour vos clients.",
				title:
					"Audits, plans d’action et portail client pour les cabinets QHSE",
				summary:
					"Pour les cabinets de conseil en qualité, sécurité et environnement qui réalisent audits, documents uniques et accompagnements à la certification.",
				audience: "Gérants de cabinets de 3 à 15 consultants",
			},
			hero: {
				label: "Cas d’usage · Conseil QHSE · DUERP · ISO 9001, 14001, 45001",
				title:
					"L’outil d’audit et de suivi des plans d’action de votre cabinet QHSE.",
				emphasis: "suivi des plans d’action",
				intro:
					"Constats saisis sur le terrain, cotation selon votre méthode, rapport à votre marque, plan d’action que vos clients mettent à jour eux-mêmes : Lezo construit l’outil de votre cabinet, et le portail client devient un service que vous facturez.",
				primaryCta: "Décrire ma méthode",
				secondaryCta: "Voir l’outil étape par étape",
			},
			facts: {
				who: {
					label: "Pour qui",
					value:
						"Cabinets de conseil QHSE et de prévention, 3 à 15 consultants",
				},
				tool: {
					label: "Ce que l’on outille",
					value:
						"Grilles d’audit, cotation, rapports, plans d’action, portail client",
				},
				first: {
					label: "Première version",
					value: "Grille d’audit sur tablette et rapport généré à votre marque",
				},
			},
			day: {
				label: "Une mission type",
				title: "Une mission d’audit, aujourd’hui",
				description:
					"Le déroulé habituel d’un audit sécurité ou d’un audit interne ISO dans une PME cliente. Le travail de fond se fait sur place ; c’est l’après qui coûte.",
				moments: {
					a: {
						time: "08:30",
						actor: "Consultante · sur site",
						text: "Visite de l’atelier et du quai d’expédition : photos sur le téléphone, constats notés sur une grille imprimée.",
						friction:
							"Une grille Excel différente pour chaque client et chaque référentiel.",
					},
					b: {
						time: "14:00",
						actor: "Consultante · restitution",
						text: "Restitution à chaud avec le dirigeant et le responsable QSE : les écarts majeurs sont annoncés, les pilotes désignés oralement.",
						friction:
							"Les engagements pris en réunion ne sont écrits nulle part.",
					},
					c: {
						time: "Le soir",
						actor: "Consultante · bureau",
						text: "Rédaction du rapport Word : recopier les constats, insérer les photos, reprendre la cotation, mettre en forme le plan d’action.",
						friction:
							"Une à deux journées de rédaction pour une journée d’audit.",
					},
					d: {
						time: "J+15",
						actor: "Client",
						text: "Le plan d’action arrive en pièce jointe. Le responsable QSE le transfère, puis il disparaît dans la boîte mail.",
						friction: "Personne ne sait quelles actions sont soldées.",
					},
					e: {
						time: "J+11 mois",
						actor: "Cabinet",
						text: "Avant l’audit de surveillance, il faut reconstituer ce qui a été fait : relances, preuves reçues par e-mail, versions du document unique.",
						friction:
							"La valeur du cabinet entre deux missions reste invisible.",
					},
				},
			},
			stakes: {
				label: "Ce que le métier exige",
				title: "Des obligations qui ne s’arrêtent pas au rapport",
				description:
					"Vos clients doivent prouver dans la durée ce qu’ils ont fait de vos recommandations. C’est une occasion de rester présent entre deux missions.",
				items: {
					duerp: {
						title: "Un document unique tenu à jour",
						description:
							"Obligatoire dès le premier salarié, mis à jour au moins chaque année à partir de 11 salariés, conservé 40 ans dans ses versions successives, avec un programme annuel de prévention à partir de 50 salariés.",
						ref: "Code du travail, art. L4121-3-1 et R4121-2",
					},
					iso: {
						title: "Des actions correctives vérifiées",
						description:
							"Les normes ISO 9001, 14001 et 45001 exigent audits internes, revue de direction et traitement des non-conformités. Chaque audit de surveillance vérifie les actions de l’année précédente.",
						ref: "ISO 9001, 14001 et 45001, chapitres 9 et 10",
					},
					prevention: {
						title: "Des plans de prévention à faire vivre",
						description:
							"Plans de prévention avec les entreprises extérieures, protocoles de sécurité pour le chargement et le déchargement : autant de documents à produire et à suivre chez vos clients.",
						ref: "Code du travail, art. R4512-7 et R4515-4",
					},
				},
			},
			build: {
				label: "Ce que Lezo construit",
				title: "De la visite au suivi client, dans un seul outil",
				description:
					"Un exemple d’outil pour un cabinet QHSE. Le vôtre reprend vos grilles, votre cotation et la présentation de vos rapports.",
				steps: {
					a: {
						title: "Les constats sont saisis pendant la visite",
						description:
							"Sur tablette ou téléphone, la consultante suit la grille du référentiel, photographie, qualifie chaque constat (non-conformité majeure ou mineure, écart, piste de progrès) et le rattache à une unité de travail.",
					},
					b: {
						title: "La cotation hiérarchise les risques",
						description:
							"Gravité et fréquence selon votre méthode : chaque risque se place dans la matrice, et les situations prioritaires ressortent d’elles-mêmes.",
					},
					c: {
						title: "Rapport et plan d’action sont prêts le soir même",
						description:
							"Le rapport à votre marque est généré à partir des constats. Le plan d’action reprend chaque écart avec son pilote, son échéance et la preuve attendue.",
					},
					d: {
						title: "Le client fait vivre son plan d’action",
						description:
							"Dans le portail, les pilotes mettent à jour leurs actions et déposent leurs preuves. Vous suivez tous vos clients et préparez l’audit suivant en quelques minutes.",
					},
				},
			},
			why: {
				label: "Pourquoi du sur-mesure",
				title: "Pourquoi un outil propre à votre cabinet",
				description:
					"Les logiciels QHSE du marché sont faits pour l’entreprise auditée, pas pour le cabinet qui accompagne plusieurs clients. Votre outil reprend votre méthode et vous permet de vendre du suivi.",
				items: {
					method: {
						title: "Votre méthode, pas celle d’un éditeur",
						description:
							"Vos grilles par référentiel, votre échelle de cotation, vos libellés : votre savoir-faire reste le vôtre.",
					},
					report: {
						title: "Des rapports à votre marque",
						description:
							"Le rapport sort à votre présentation, sans recopie ni mise en page.",
					},
					clients: {
						title: "Des clients invités sans licence",
						description:
							"Dirigeants et pilotes accèdent au portail sans coût par utilisateur.",
					},
					revenue: {
						title: "Une nouvelle ligne de chiffre d’affaires",
						description:
							"Le suivi des plans d’action devient un abonnement que vous proposez à la fin de chaque mission.",
					},
				},
				visual: {
					title: "Le portail de suivi, rentabilisé en un an",
					revenue: "Abonnements de suivi cumulés",
					cost: "Première version",
					payback: "Couverte au mois {month}",
					months: "Mois",
					summary:
						"Avec cette hypothèse, les abonnements cumulés couvrent la première version ({cost}) au mois {month}, et atteignent {total} sur douze mois.",
					note: "Hypothèse indicative : un abonnement de suivi à {price} par mois et par client, {clients} nouveaux clients abonnés chaque mois.",
				},
			},
			scope: {
				first: {
					label: "Première version",
					title: "Ce que contiendrait une première version",
					items: {
						a: "Votre grille d’audit principale, sur tablette et téléphone.",
						b: "Votre méthode de cotation et la matrice de criticité.",
						c: "Le rapport généré à votre marque, avec photos et plan d’action.",
						d: "Le suivi des actions par client, avec relances automatiques.",
					},
				},
				check: {
					label: "Au cadrage",
					title: "Ce que nous vérifions ensemble",
					items: {
						a: "Les référentiels et les grilles à reprendre en premier.",
						b: "Votre échelle de cotation et vos règles de priorité.",
						c: "Le portail client : intérêt de vos clients et prix du suivi.",
						d: "L’hébergement et la confidentialité des données de vos clients.",
					},
				},
			},
			faq: {
				title: "Vos questions sur un outil d’audit sur mesure",
				items: {
					excel: {
						question: "Pourquoi changer, si Excel et Word suffisent ?",
						answer:
							"Ils suffisent pour produire un rapport. Ils ne permettent ni de suivre les actions de vingt clients, ni de prouver ce qui a été fait au moment de l’audit suivant. L’outil reprend vos modèles : vous ne changez pas de méthode, vous arrêtez de recopier.",
					},
					portal: {
						question: "Mes clients utiliseront-ils vraiment un portail ?",
						answer:
							"Le portail ne demande au pilote que de mettre à jour une action et de joindre une preuve, depuis un lien reçu par e-mail. La première version peut démarrer avec deux ou trois clients volontaires avant d’être proposée à tous.",
					},
					price: {
						question: "20 000 €, n’est-ce pas beaucoup pour un cabinet ?",
						answer:
							"C’est l’ordre de grandeur d’une première version. Elle se rembourse par le temps de rédaction gagné et, si vous le souhaitez, par l’abonnement de suivi vendu à vos clients. Le cadrage chiffre précisément le périmètre.",
					},
					data: {
						question: "Où sont hébergées les données de nos clients ?",
						answer:
							"En Europe, chez un hébergeur choisi avec vous. Le cabinet reste maître des données, et chaque client n’accède qu’à son propre dossier.",
					},
					duerp: {
						question: "L’outil peut-il gérer le document unique ?",
						answer:
							"Il peut versionner chaque mise à jour du document unique et en conserver l’historique, comme l’exige le code du travail. Le périmètre exact se décide au cadrage, selon vos missions.",
					},
				},
			},
			cta: {
				title: "Comment suivez-vous les plans d’action de vos clients ?",
				description:
					"Vos référentiels, votre méthode de cotation, le nombre de clients suivis : quelques lignes suffisent pour préparer l’échange.",
				button: "Décrire ma méthode",
			},
			scene: {
				labels: {
					findings: "Constats",
					rating: "Cotation",
					plan: "Plan d’action",
					portal: "Portail client",
				},
				host: "audit.votre-cabinet.fr",
				findings: {
					title: "Audit · Conserverie Les Trois Rives",
					subtitle: "ISO 45001 · audit interne",
					photos: "12 photos",
					items: {
						a: {
							tag: "NC majeure",
							ref: "§9.2",
							text: "Aucun audit interne depuis 14 mois",
						},
						b: {
							tag: "NC mineure",
							ref: "§7.2",
							text: "Habilitation électrique expirée pour deux opérateurs",
						},
						c: {
							tag: "Écart",
							ref: "DUERP",
							text: "Pas de protocole de sécurité au quai d’expédition",
						},
						d: {
							tag: "Piste",
							ref: "§9.3",
							text: "Suivre les presqu’accidents en revue de direction",
						},
					},
				},
				rating: {
					title: "Matrice de criticité",
					gravity: "Gravité",
					frequency: "Fréquence",
					priority: "Prioritaire à partir de 12",
					risks: {
						a: { name: "Autoclave · brûlure vapeur", score: "G4 × F3 = 12" },
						b: { name: "Quai · chargement", score: "G3 × F3 = 9" },
						c: { name: "Chantier R+2 · chute", score: "G4 × F2 = 8" },
					},
				},
				plan: {
					title: "Plan d’action",
					columns: { action: "Action", owner: "Pilote", due: "Échéance" },
					rows: {
						a: {
							action: "Poser le carter de protection",
							owner: "Maintenance",
							due: "15/11",
						},
						b: {
							action: "Planifier l’audit interne",
							owner: "C. Martin",
							due: "30/11",
						},
						c: {
							action: "Rédiger le protocole de sécurité",
							owner: "Logistique",
							due: "06/12",
						},
					},
					report: "Rapport d’audit · 18 pages",
					generated: "Généré",
				},
				portal: {
					host: "suivi.votre-cabinet.fr",
					title: "Les Trois Rives · plan d’action",
					stats: { done: "Soldées", progress: "En cours", late: "En retard" },
					action: "Poser le carter de protection",
					proof: "Preuve déposée par le pilote · photo",
					closed: "Soldée",
					nextAudit: "Prochain audit de surveillance · 12/03",
				},
			},
		},
		cleaning: {
			card: {
				name: "Propreté",
				navDescription:
					"Contrôle qualité multi-sites et preuve de service au client.",
				title:
					"Contrôle qualité multi-sites et preuve de service pour les entreprises de propreté",
				summary:
					"Pour les entreprises de propreté qui doivent prouver la qualité de leurs prestations à chaque donneur d’ordre, et la défendre au renouvellement.",
				audience:
					"Directions d’agence, responsables qualité et chefs de secteur",
			},
			hero: {
				label: "Cas d’usage · Propreté · Contrôle qualité · Multi-sites",
				title:
					"Un logiciel de contrôle qualité pour prouver la propreté de chaque site.",
				emphasis: "prouver la propreté",
				intro:
					"Grilles alignées sur chaque cahier des charges, score calculé sur place, non-conformités suivies jusqu’à leur levée, espace dédié au donneur d’ordre : Lezo construit l’outil qui transforme vos contrôles en dossier de renouvellement.",
				primaryCta: "Décrire mes contrôles",
				secondaryCta: "Voir l’outil étape par étape",
			},
			facts: {
				who: {
					label: "Pour qui",
					value:
						"Entreprises de propreté B2B : bureaux, santé, copropriétés, industrie",
				},
				tool: {
					label: "Ce que l’on outille",
					value:
						"Grilles de contrôle, scores, non-conformités, rapports, portail client",
				},
				first: {
					label: "Première version",
					value: "Contrôle sur mobile et rapport mensuel par site",
				},
			},
			day: {
				label: "Une journée type",
				title: "Une journée de chef de secteur, aujourd’hui",
				description:
					"Un chef de secteur suit souvent plusieurs sites dans la journée, entre contrôles, remplacements et rendez-vous clients. La qualité se joue sur le terrain, mais elle se prouve sur papier.",
				moments: {
					a: {
						time: "06:45",
						actor: "Chef de secteur",
						text: "Deux agents absents sur un immeuble de bureaux : il organise les remplacements par téléphone avant l’arrivée des premiers occupants.",
						friction:
							"Un passage non réalisé ne sera signalé au client que s’il le remarque.",
					},
					b: {
						time: "09:30",
						actor: "Chef de secteur · contrôle",
						text: "Contrôle des sanitaires et de l’accueil avec une grille papier : sols, consommables, surfaces de contact, odeur.",
						friction:
							"La grille ne correspond pas exactement au cahier des charges du client.",
					},
					c: {
						time: "11:00",
						actor: "Chef de secteur · clinique",
						text: "À la clinique, de la poussière sur un rail lumineux en chambre : il prévient la cheffe d’équipe et repart vers le site suivant.",
						friction:
							"La non-conformité n’a ni responsable, ni échéance, ni preuve de levée.",
					},
					d: {
						time: "15:00",
						actor: "Donneur d’ordre",
						text: "Le responsable des services généraux d’un client se plaint de la cafétéria et demande un compte rendu des contrôles du trimestre.",
						friction:
							"Les contrôles sont dans des classeurs, dans trois agences.",
					},
					e: {
						time: "Renouvellement",
						actor: "Direction",
						text: "L’appel d’offres est relancé. Le mémoire technique doit démontrer la qualité des dernières années.",
						friction:
							"Sans historique chiffré, la qualité se défend avec des mots.",
					},
				},
			},
			stakes: {
				label: "Ce qui se joue",
				title: "Ce qui se joue à chaque renouvellement",
				description:
					"Quand un marché change de prestataire, le personnel du site suit généralement le contrat. Ce qui distingue une entreprise, c’est son encadrement et sa capacité à prouver la qualité.",
				items: {
					transfer: {
						title: "Le personnel suit le contrat",
						description:
							"En cas de changement de prestataire, les salariés affectés au site sont repris par l’entreprise entrante, sous conditions. La différence se fait sur l’organisation et la preuve.",
						ref: "Convention collective de la propreté (IDCC 3043), article 7",
					},
					measure: {
						title: "Une qualité mesurée, pas déclarée",
						description:
							"Les cahiers des charges prévoient des contrôles par échantillonnage, avec niveaux de qualité, notation et actions correctives, souvent en référence aux normes du secteur.",
						ref: "NF EN 13549 · NF X50-794-1",
					},
					health: {
						title: "Des exigences renforcées en santé",
						description:
							"En établissement de santé, chaque zone a son niveau de risque, ses protocoles de bionettoyage et ses fréquences, validés par l’établissement.",
						ref: "Zones de risque 1 à 4 · NF S90-351",
					},
				},
			},
			build: {
				label: "Ce que Lezo construit",
				title: "Du contrôle terrain au rapport client, dans un seul outil",
				description:
					"Un exemple d’outil pour une entreprise de propreté multi-sites. Le vôtre reprend vos grilles, vos seuils contractuels et l’organisation de vos agences.",
				steps: {
					a: {
						title: "Une grille par site, celle du contrat",
						description:
							"Le chef de secteur contrôle zone par zone avec la grille du cahier des charges du client : conforme, non conforme, photo si besoin, même sans réseau.",
					},
					b: {
						title: "Le score est calculé sur place",
						description:
							"Dès la fin du contrôle, le score du site et de chaque zone s’affiche, comparé au seuil prévu au contrat.",
					},
					c: {
						title: "Chaque non-conformité a un responsable",
						description:
							"La non-conformité part vers la cheffe d’équipe avec la photo et une échéance. La levée se prouve par une nouvelle photo, horodatée.",
					},
					d: {
						title: "Le donneur d’ordre voit la qualité, mois après mois",
						description:
							"Dans son espace, le client consulte les scores, les contrôles et les actions correctives de ses sites. Le rapport mensuel part sans être rédigé.",
					},
				},
			},
			why: {
				label: "Pourquoi du sur-mesure",
				title: "Pourquoi un outil fait pour vos contrats",
				description:
					"Les applications de pointage et de formulaires imposent leur grille. Votre outil reprend celle de chaque client et construit, contrôle après contrôle, la preuve dont vous aurez besoin au renouvellement.",
				items: {
					grid: {
						title: "La grille de chaque client",
						description:
							"Zones, critères, fréquences et seuils propres à chaque contrat, sans compromis.",
					},
					loop: {
						title: "Des non-conformités qui se ferment",
						description:
							"Photo, responsable, échéance, levée prouvée : les pénalités se discutent sur des faits.",
					},
					portal: {
						title: "Un client qui voit, sans relancer",
						description:
							"Scores, photos et actions disponibles à tout moment pour le donneur d’ordre.",
					},
					field: {
						title: "Pensé pour le terrain",
						description:
							"Pictogrammes, photos et saisie rapide, y compris en sous-sol, sans réseau.",
					},
				},
				visual: {
					title: "Le dossier de renouvellement se construit seul",
					series: "Score mensuel · Tour Lumen",
					threshold: "Seuil contractuel {value}",
					months: "N D J F M A M J J A S O",
					average: "Moyenne sur 12 mois",
					closing: "Délai moyen de levée",
					closingValue: "26 h",
					controls: "Contrôles réalisés",
					controlsValue: "48",
					note: "Illustration aux données fictives : douze mois de contrôles sur un site.",
				},
			},
			scope: {
				first: {
					label: "Première version",
					title: "Ce que contiendrait une première version",
					items: {
						a: "La grille de contrôle de vos principaux contrats, utilisable hors réseau.",
						b: "Le score par zone et par site, comparé au seuil contractuel.",
						c: "Le suivi des non-conformités jusqu’à leur levée.",
						d: "Le rapport mensuel par site, prêt à envoyer.",
					},
				},
				check: {
					label: "Au cadrage",
					title: "Ce que nous vérifions ensemble",
					items: {
						a: "Les grilles et les seuils de vos contrats les plus exigeants.",
						b: "L’organisation des contrôles : qui contrôle, à quelle fréquence, sur quels sites.",
						c: "Le portail donneur d’ordre : ce que vos clients voient, et ce qui reste interne.",
						d: "Les outils existants : pointage, planning, paie.",
					},
				},
			},
			faq: {
				title: "Vos questions sur un outil de contrôle qualité sur mesure",
				items: {
					cheap: {
						question:
							"Des applications de contrôle existent pour quelques dizaines d’euros par mois. Pourquoi du sur-mesure ?",
						answer:
							"Pour un premier besoin de traçabilité, elles conviennent. Le sur-mesure se justifie quand chaque client a sa grille, ses seuils et ses exigences de rapport, et quand la qualité doit devenir un argument au renouvellement. Le cadrage compare les deux options.",
					},
					exposure: {
						question:
							"Un portail client ne va-t-il pas exposer nos non-conformités ?",
						answer:
							"Vous choisissez ce que voit le client. Montrer une non-conformité levée en quelques heures rassure souvent davantage qu’un contrôle parfait sur le papier.",
					},
					field: {
						question:
							"Nos chefs d’équipe sont peu à l’aise avec le numérique. Est-ce un problème ?",
						answer:
							"L’application est conçue pour eux : grandes cases, pictogrammes, photos, très peu de texte à saisir. Elle est testée avec eux sur de vrais contrôles avant le déploiement.",
					},
					client: {
						question: "Certains clients ont leur propre grille de contrôle.",
						answer:
							"C’est justement le cas que l’outil traite : chaque contrat peut avoir sa grille, ses critères et son seuil, y compris pour un contrôle contradictoire avec le client.",
					},
					network: {
						question: "Et dans les sous-sols, sans réseau ?",
						answer:
							"Les contrôles sont enregistrés sur le téléphone et envoyés dès que la connexion revient.",
					},
				},
			},
			cta: {
				title: "Comment prouvez-vous la qualité de vos prestations ?",
				description:
					"Nombre de sites, types de contrats, façon de contrôler : quelques lignes suffisent pour préparer l’échange.",
				button: "Décrire mes contrôles",
			},
			scene: {
				labels: {
					control: "Contrôle",
					score: "Score",
					fix: "Non-conformité",
					portal: "Portail client",
				},
				host: "qualite.votre-entreprise.fr",
				control: {
					title: "Contrôle · Tour Lumen",
					zone: "Sanitaires H/F · niveau 2",
					photo: "Photo jointe au critère",
					next: "Zone suivante",
					criteria: {
						floors: "Sols sans traces",
						bins: "Corbeilles vidées",
						contact: "Surfaces de contact",
						supplies: "Consommables",
						mirrors: "Miroirs",
					},
				},
				score: {
					title: "Score du contrôle",
					site: "Tour Lumen · 14 zones",
					threshold: "Seuil 90 %",
					zones: {
						reception: "Accueil",
						office: "Open space niv. 3",
						sanitary: "Sanitaires niv. 2",
						cafeteria: "Cafétéria",
					},
				},
				fix: {
					title: "Non-conformité · NC-0412",
					text: "Distributeur de savon vide",
					zone: "Tour Lumen · sanitaires niveau 2",
					before: "Constat",
					after: "Levée",
					owner: "K. Mbaye · cheffe d’équipe",
					due: "Échéance aujourd’hui, 14:00",
					action: "Réapprovisionner et ajouter au passage de 14 h",
					lifted: "Levée en 3 h",
				},
				portal: {
					title: "Groupe Nordis · vos sites",
					period: "Douze derniers mois",
					threshold: "Seuil 90 %",
					report: "Rapport d’octobre envoyé",
					open: "2 NC ouvertes",
					sites: {
						a: "Tour Lumen",
						b: "Siège Nordis",
						c: "Agence Part-Dieu",
					},
				},
			},
		},
		takeover: {
			card: {
				name: "Reprise d’app React Native",
				navDescription:
					"Une application orpheline à remettre en état sur les stores.",
				title:
					"Reprendre une application React Native après le départ du prestataire",
				summary:
					"Pour les entreprises dont l’application mobile a été livrée par une agence ou un freelance parti depuis, et que plus personne ne sait construire ni publier.",
				audience: "Dirigeants et CTO de PME et de start-up sans équipe mobile",
			},
			hero: {
				label: "Cas d’usage · React Native · Expo · App Store et Google Play",
				title: "Reprendre votre application React Native, sans tout réécrire.",
				emphasis: "sans tout réécrire",
				intro:
					"Votre prestataire est parti, le build ne passe plus et les stores imposent une mise à jour ? Lezo récupère vos accès, diagnostique l’application, la remet en conformité sur Expo et vous rend une chaîne de publication que vous maîtrisez.",
				primaryCta: "Faire le point sur mon application",
				secondaryCta: "Voir la reprise étape par étape",
			},
			facts: {
				who: {
					label: "Pour qui",
					value:
						"PME et start-up dont l’application a été livrée par un prestataire parti",
				},
				tool: {
					label: "Ce que l’on reprend",
					value: "Accès, diagnostic, migration Expo, builds et publication",
				},
				first: {
					label: "Première étape",
					value: "Un diagnostic écrit, avec une date et un prix",
				},
			},
			day: {
				label: "Un scénario fréquent",
				title: "Comment une application devient orpheline",
				description:
					"Rien ne casse d’un coup : chaque mois éloigne un peu plus l’application de ce que les stores acceptent, jusqu’au jour où une mise à jour devient obligatoire.",
				moments: {
					a: {
						time: "Livraison",
						actor: "Agence",
						text: "L’application est livrée et publiée. Les comptes développeur, les certificats et la clé de signature restent chez l’agence, « pour simplifier ».",
						friction: "Rien n’est à votre nom, et rien n’est documenté.",
					},
					b: {
						time: "Un an après",
						actor: "Freelance",
						text: "Le développeur qui connaissait le projet change de mission. Les petites corrections passaient par des mises à jour à chaud.",
						friction:
							"Le service de mise à jour utilisé, CodePush, a fermé en mars 2025.",
					},
					c: {
						time: "Fin d’été",
						actor: "Google Play",
						text: "La Play Console annonce que l’application doit cibler un niveau d’API plus récent avant la date limite.",
						friction:
							"Passé l’échéance, les nouveaux utilisateurs d’appareils récents ne la trouvent plus.",
					},
					d: {
						time: "Le lendemain",
						actor: "Dirigeant",
						text: "Un nouveau développeur tente de lancer le build : version de Xcode refusée, dépendances abandonnées, keystore introuvable.",
						friction: "Plus personne ne sait construire l’application.",
					},
					e: {
						time: "Une semaine après",
						actor: "Agence",
						text: "Le devis arrive : tout réécrire, plusieurs mois de travail et un budget à cinq chiffres.",
						friction: "Le code métier fonctionne pourtant toujours.",
					},
				},
			},
			stakes: {
				label: "Ce que les stores exigent",
				title: "Des exigences qui reviennent chaque année",
				description:
					"Une application mobile ne peut pas rester figée : Google et Apple relèvent régulièrement leurs exigences techniques, à des dates précises.",
				items: {
					android: {
						title: "Google Play relève le niveau d’API chaque année",
						description:
							"Chaque fin août, Google Play exige un niveau d’API plus récent pour les mises à jour : Android 16 (API 36) depuis le 31 août 2026. Au-delà, l’application n’est plus proposée aux nouveaux utilisateurs d’appareils récents.",
						ref: "Google Play · exigences de niveau d’API cible",
					},
					ios: {
						title: "L’App Store impose une version de Xcode",
						description:
							"Pour publier une mise à jour, Apple exige une version récente de Xcode et du SDK iOS : Xcode 26 et le SDK iOS 26 depuis le 28 avril 2026. Un projet ancien doit d’abord être remis à niveau.",
						ref: "Apple Developer · exigences de soumission",
					},
					ownership: {
						title: "Des comptes et des clés à votre nom",
						description:
							"Le compte développeur, la clé de signature Android et les certificats Apple conditionnent toute mise à jour. Apple et Google prévoient le transfert d’une application sans perdre ni utilisateurs ni avis.",
						ref: "Play App Signing · transfert d’application",
					},
				},
			},
			build: {
				label: "Ce que Lezo fait",
				title: "La reprise, étape par étape",
				description:
					"La méthode d’une migration Expo, précédée d’une étape souvent oubliée : récupérer ce qui vous appartient.",
				steps: {
					a: {
						title: "Récupérer vos accès",
						description:
							"Dépôt de code, comptes App Store Connect et Play Console, clé de signature, certificats, Firebase, variables d’environnement : nous faisons l’inventaire et transférons tout à votre nom.",
					},
					b: {
						title: "Diagnostiquer avant de chiffrer",
						description:
							"Chaque dépendance native est examinée : module Expo équivalent, plugin à écrire ou code natif conservé. Vous recevez un diagnostic écrit, une date et un prix.",
					},
					c: {
						title: "Remettre l’application en conformité",
						description:
							"L’application passe sur Expo et sur la nouvelle architecture de React Native, cible les niveaux d’API et les SDK exigés, et les mises à jour à chaud reprennent avec EAS Update.",
					},
					d: {
						title: "Publier, puis transmettre",
						description:
							"Les builds et la soumission aux stores sont automatisés. Votre équipe ou un nouveau prestataire reprend la main avec la documentation, ou Lezo assure la maintenance.",
					},
				},
			},
			why: {
				label: "Reprendre ou réécrire",
				title: "Reprendre coûte moins cher que tout refaire",
				description:
					"Les écrans, la logique métier et les données de votre application fonctionnent toujours. Ce qui a vieilli, c’est l’outillage autour : c’est lui que nous remplaçons.",
				items: {
					keep: {
						title: "Le code applicatif est conservé",
						description:
							"Écrans, logique métier et données restent en place : vos utilisateurs mettent à jour l’application comme d’habitude.",
					},
					own: {
						title: "Vous redevenez propriétaire",
						description:
							"Comptes, clés, certificats et dépôt à votre nom, documentés : votre application ne dépend plus d’un prestataire.",
					},
					date: {
						title: "Une date et un prix d’abord",
						description:
							"Le diagnostic fixe le périmètre avant tout engagement, en tenant compte des échéances des stores.",
					},
					later: {
						title: "Une maintenance plus légère ensuite",
						description:
							"Sur Expo, les montées de version et les publications redeviennent des tâches courantes.",
					},
				},
			},
			scope: {
				first: {
					label: "Diagnostic",
					title: "Ce que contient le diagnostic",
					items: {
						a: "L’inventaire des accès, et le plan pour les transférer à votre nom.",
						b: "L’état du projet : versions, builds iOS et Android, dépendances natives.",
						c: "Les échéances des stores qui concernent votre application.",
						d: "Le plan de reprise, son calendrier et son prix.",
					},
				},
				check: {
					label: "Pour démarrer",
					title: "Ce dont nous avons besoin",
					items: {
						a: "Un accès en lecture au dépôt de code, ou une archive du projet.",
						b: "Les identifiants des comptes développeur, ou le contact de leur titulaire.",
						c: "La liste des services utilisés : backend, notifications, paiements, mesure d’audience.",
						d: "Un interlocuteur qui connaît l’usage de l’application.",
					},
				},
			},
			faq: {
				title: "Vos questions sur la reprise d’une application",
				items: {
					rewrite: {
						question: "Faut-il réécrire l’application ?",
						answer:
							"Rarement. L’essentiel du code applicatif est conservé : ce sont la configuration native, certaines dépendances et la chaîne de build qui changent. Le diagnostic identifie les parties qui demandent davantage.",
					},
					keystore: {
						question:
							"Nous avons perdu la clé de signature Android. Est-ce grave ?",
						answer:
							"Si l’application utilise Play App Signing, Google peut réinitialiser la clé d’upload et les mises à jour restent possibles. Sinon, la situation est plus délicate : c’est le premier point que vérifie le diagnostic.",
					},
					accounts: {
						question:
							"Les comptes sont au nom de l’ancienne agence. Que faire ?",
						answer:
							"Apple et Google permettent de transférer une application vers votre propre compte, sans perdre les utilisateurs ni les avis. Nous préparons la demande et vous accompagnons.",
					},
					deadline: {
						question:
							"L’échéance de Google Play est passée ou approche. A-t-on le temps ?",
						answer:
							"Google permet de demander une prolongation, jusqu’au 1er novembre pour l’échéance d’août. Le diagnostic commence par ce qui bloque la publication, pour livrer au plus vite une mise à jour conforme.",
					},
					users: {
						question: "Les utilisateurs vont-ils perdre leurs données ?",
						answer:
							"Non. L’application garde son identifiant et sa signature : c’est une mise à jour normale, installée par-dessus la version existante.",
					},
				},
			},
			cta: {
				title: "Où en est votre application ?",
				description:
					"Qui l’a développée, quand a-t-elle été publiée pour la dernière fois, qu’est-ce qui bloque ? Ces trois réponses suffisent pour un premier échange.",
				button: "Faire le point sur mon application",
			},
			scene: {
				labels: {
					access: "Accès",
					diagnosis: "Diagnostic",
					compliance: "Conformité",
					release: "Publication",
				},
				access: {
					file: "inventaire-acces.md",
					title: "Inventaire des accès",
					owner: "Tout est désormais à votre nom",
					items: {
						repo: { name: "Dépôt Git", status: "Transféré" },
						apple: { name: "App Store Connect", status: "Transféré" },
						play: { name: "Play Console", status: "Transféré" },
						key: { name: "Clé d’upload Android", status: "Réinitialisée" },
						firebase: { name: "Firebase", status: "Récupéré" },
					},
				},
				diagnosis: {
					file: "package.json",
					title: "Dépendances natives",
					summary: "3 remplacées, 1 conservée · code applicatif inchangé",
					rows: {
						a: { from: "react-native-code-push", to: "EAS Update" },
						b: { from: "react-native-camera", to: "expo-camera" },
						c: { from: "react-native-splash-screen", to: "expo-splash-screen" },
						d: { from: "module-paiement-maison", to: "Natif conservé" },
					},
				},
				compliance: {
					file: "app.config.ts",
					title: "Exigences des stores",
					compliant: "Conforme pour publier",
					rows: {
						android: { label: "Google Play · API cible", from: "34", to: "36" },
						ios: { label: "App Store · SDK iOS", from: "17", to: "26" },
						pages: { label: "Pages mémoire de 16 Ko", from: "non", to: "oui" },
						arch: { label: "Nouvelle architecture", from: "non", to: "oui" },
					},
				},
				release: {
					appName: "Votre application",
					version: "Version 3.0.0",
					update: "Mettre à jour",
					whatsNew: "Nouveautés",
					notes:
						"Compatibilité avec les derniers appareils, corrections et améliorations.",
					published: "Publiée sur l’App Store et Google Play",
					builds: {
						ios: "Build iOS · EAS",
						android: "Build Android · EAS",
						submit: "Soumission aux stores",
					},
				},
			},
		},
	},
};
